/**
 * Speech synthesis utility for consistent voice across the application
 */

// Store the selected voice to ensure consistency
let selectedVoice: SpeechSynthesisVoice | null = null;

// Track if speech is ongoing
let isSpeaking = false;

/**
 * Initialize speech synthesis and select the preferred voice
 */
export const initSpeechSynthesis = (): void => {
  // Reset any ongoing speech
  window.speechSynthesis.cancel();
  
  console.log('Initializing speech synthesis...');
  
  // Get available voices
  let voices = window.speechSynthesis.getVoices();
  
  // If no voices available yet, set up a listener
  if (voices.length === 0) {
    window.speechSynthesis.onvoiceschanged = () => {
      voices = window.speechSynthesis.getVoices();
      selectPreferredVoice(voices);
    };
  } else {
    selectPreferredVoice(voices);
  }
};

/**
 * Select the preferred voice for the application
 */
const selectPreferredVoice = (voices: SpeechSynthesisVoice[]): void => {
  // Look for Google US English voice
  selectedVoice = voices.find(v => 
    v.name === 'Google US English' || 
    (v.name.includes('Google') && v.lang === 'en-US')
  ) || null;
};

/**
 * Speak text with the standardized voice
 * @param text Text to speak
 * @param callback Optional callback for when speech completes
 */
export const speak = (text: string, callback?: () => void): void => {
  // Skip empty text
  if (!text || text.trim() === '') {
    if (callback) callback();
    return;
  }
  
  // Always cancel any previous speech first
  cancelSpeech();
  
  // Mark as speaking
  isSpeaking = true;
  
  // Create a new utterance
  const utterance = new SpeechSynthesisUtterance(text);
  
  // Apply standardized voice and settings
  if (selectedVoice) {
    utterance.voice = selectedVoice;
  }
  
  // Standardized speech settings
  utterance.rate = 0.9; // Slightly slower for clarity
  utterance.pitch = 1.0;
  utterance.volume = 1.0;
  
  // Add completion handler
  utterance.onend = () => {
    // Mark as no longer speaking
    isSpeaking = false;
    
    // Call the callback if provided
    if (callback) {
      callback();
    }
  };
  
  // Add error handler
  utterance.onerror = () => {
    // Mark as no longer speaking
    isSpeaking = false;
    
    // Call callback even on error to ensure state is updated
    if (callback) {
      callback();
    }
  };
  
  // Speak the text
  window.speechSynthesis.speak(utterance);
};

/**
 * Cancels any ongoing speech synthesis
 */
export const cancelSpeech = (): void => {
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
    
    // Reset speaking state
    isSpeaking = false;
  }
};

/**
 * Test the speech synthesis system with a short utterance
 */
export const testSpeech = (): void => {
  speak('Testing speech synthesis system');
}; 